import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, Loader2, Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useContactSearch } from "@/modules/contacts/hooks/useContacts";
import { NewContactDrawer } from "@/modules/contacts/components/NewContactDrawer";

type Tab = "internal" | "external";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  existingUserIds: string[];
  existingContactIds: string[];
  onAdd: (payload: {
    userIds?: string[];
    contactIds?: string[];
  }) => Promise<void> | void;
};

export function AddAttendeeModal({
  isOpen,
  onClose,
  existingUserIds,
  existingContactIds,
  onAdd,
}: Props) {
  const [tab, setTab] = useState<Tab>("internal");
  const [search, setSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(
    new Set(),
  );
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const usersQuery = useUserList({ page: 1, limit: 100 });
  const contactsQuery = useContactSearch(search, tab === "external");

  const users = useMemo(() => {
    const all = usersQuery.data?.data ?? [];
    if (!search.trim()) return all;
    const term = search.toLowerCase();
    return all.filter(
      (u) =>
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term),
    );
  }, [usersQuery.data, search]);

  const contacts = contactsQuery.data?.data ?? [];

  function toggleUser(id: string) {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleContact(id: string) {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleAdd() {
    if (selectedUsers.size === 0 && selectedContacts.size === 0) return;
    setSubmitting(true);
    try {
      await onAdd({
        userIds:
          selectedUsers.size > 0 ? Array.from(selectedUsers) : undefined,
        contactIds:
          selectedContacts.size > 0 ? Array.from(selectedContacts) : undefined,
      });
      setSelectedUsers(new Set());
      setSelectedContacts(new Set());
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-[9999] flex -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border bg-white shadow-2xl"
        style={{
          width: 520,
          height: 480,
          borderColor: "hsl(210,30%,85%)",
        }}
      >
        <div
          className="flex items-center justify-between border-b px-5 py-3"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-base font-semibold text-foreground">
            Add Attendees
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div
          className="flex items-center gap-1 border-b px-2"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          {(["internal", "external"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "internal" ? "Internal (Team Members)" : "External (Contacts)"}
            </button>
          ))}
        </div>

        <div className="px-4 py-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              placeholder={
                tab === "internal" ? "Search users..." : "Search contacts..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-9 text-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2">
          {tab === "internal" ? (
            usersQuery.isLoading ? (
              <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
                Loading…
              </div>
            ) : users.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                No users found.
              </div>
            ) : (
              <div className="space-y-0.5">
                {users.map((u) => {
                  const added = existingUserIds.includes(u.id);
                  const selected = selectedUsers.has(u.id);
                  return (
                    <label
                      key={u.id}
                      className={`flex items-center gap-3 rounded-md px-3 py-2 ${
                        added
                          ? "opacity-60"
                          : "cursor-pointer hover:bg-[hsl(210,50%,97%)]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        disabled={added}
                        checked={added || selected}
                        onChange={() => !added && toggleUser(u.id)}
                      />
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-[11px] font-semibold text-[hsl(210,100%,28%)]">
                        {u.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {u.name}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {u.email}
                        </div>
                      </div>
                      {added && (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={{
                            background: "hsl(142,55%,92%)",
                            color: "hsl(142,72%,28%)",
                          }}
                        >
                          Added
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )
          ) : contactsQuery.isLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                strokeWidth={1.5}
              />
              Loading…
            </div>
          ) : contacts.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No contacts found.
            </div>
          ) : (
            <div className="space-y-0.5">
              {contacts.map((c) => {
                const added = existingContactIds.includes(c.id);
                const selected = selectedContacts.has(c.id);
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 rounded-md px-3 py-2 ${
                      added
                        ? "opacity-60"
                        : "cursor-pointer hover:bg-[hsl(210,50%,97%)]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={added}
                      checked={added || selected}
                      onChange={() => !added && toggleContact(c.id)}
                    />
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(38,92%,92%)] text-[11px] font-semibold text-[hsl(38,92%,30%)]">
                      {c.fullName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {c.fullName}
                      </div>
                      <div className="flex items-center gap-2 truncate text-xs text-muted-foreground">
                        {c.companyName && (
                          <span className="inline-flex items-center gap-1">
                            <Building2
                              className="h-3 w-3"
                              strokeWidth={1.5}
                            />
                            {c.companyName}
                          </span>
                        )}
                        {c.email && <span>· {c.email}</span>}
                      </div>
                    </div>
                    {added && (
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                        style={{
                          background: "hsl(142,55%,92%)",
                          color: "hsl(142,72%,28%)",
                        }}
                      >
                        Added
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {tab === "external" && (
          <div
            className="border-t px-4 py-2"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <button
              type="button"
              onClick={() => setNewContactOpen(true)}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
              New Contact
            </button>
          </div>
        )}

        <div
          className="flex items-center justify-end gap-2 border-t px-4 py-3"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={
              submitting ||
              (selectedUsers.size === 0 && selectedContacts.size === 0)
            }
          >
            {submitting && (
              <Loader2
                className="mr-1 h-3.5 w-3.5 animate-spin"
                strokeWidth={1.5}
              />
            )}
            Add Selected
          </Button>
        </div>
      </div>

      <NewContactDrawer
        isOpen={newContactOpen}
        onClose={() => setNewContactOpen(false)}
        onCreated={(c) => {
          setSelectedContacts((prev) => new Set([...prev, c.id]));
          setTab("external");
        }}
      />
    </>,
    document.body,
  );
}
